import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import PullToRefresh from '../components/PullToRefresh';
import { authAxios } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';

export default function EventLog() {
  const { currentSeason, API } = useContext(AppContext);
  const [events, setEvents] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [contestants, setContestants] = useState({});
  const [tribes, setTribes] = useState({});
  const [users, setUsers] = useState({});
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Multi-select filters
  const [filterPlayers, setFilterPlayers] = useState([]);
  const [filterContestants, setFilterContestants] = useState([]);
  const [filterTribes, setFilterTribes] = useState([]);
  const [filterEpisodes, setFilterEpisodes] = useState([]);

  useEffect(() => {
    if (currentSeason?.id) {
      loadData();
    }
  }, [currentSeason?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data } = await authAxios.get(`${API}/seasons/${currentSeason.id}/event-log-bundle`);
      const eventsRes = { data: data.events };
      const episodesRes = { data: data.episodes };
      const contestantsRes = { data: data.contestants };
      const tribesRes = { data: data.tribes };
      const rostersRes = { data: data.rosters };
      const usersRes = { data: data.users };
      
      setEvents(eventsRes.data);
      setEpisodes(episodesRes.data);
      setRosters(rostersRes.data);
      
      // Create maps
      const contestantsMap = {};
      contestantsRes.data.forEach(c => { contestantsMap[c.id] = c; });
      setContestants(contestantsMap);
      
      const tribesMap = {};
      tribesRes.data.forEach(t => { tribesMap[t.id] = t; });
      setTribes(tribesMap);
      
      // Filter users to only those in the current season
      // Get user IDs from rosters (players who have contestants assigned)
      const rosterUserIds = new Set(rostersRes.data.map(r => r.user_id));
      
      // Also get user IDs from events (for bonus questions)
      const eventUserIds = new Set(
        eventsRes.data
          .filter(e => e.player_user_id)
          .map(e => e.player_user_id)
      );
      
      // Combine all relevant user IDs for this season
      const seasonUserIds = new Set([...rosterUserIds, ...eventUserIds]);
      
      // Filter users to only include those in this season
      const usersMap = {};
      usersRes.data.forEach(u => {
        const inSeason = u.season_ids?.includes(currentSeason.id) || seasonUserIds.has(u.id);
        if (inSeason) {
          usersMap[u.id] = u;
        }
      });
      setUsers(usersMap);
    } catch (error) {
      console.error('Failed to load event log:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions (defined before use)
  const getEpisodeNumber = (episodeId) => {
    const episode = episodes.find(ep => ep.id === episodeId);
    return episode?.number || '?';
  };

  const getContestantName = (contestantId) => {
    if (contestantId === 'BONUS') return 'Bonus';
    return contestants[contestantId]?.full_name || 'Unknown';
  };

  const getPlayerForEvent = (event) => {
    // First check if event has player_user_id (used for BONUS and all events)
    if (event.player_user_id) {
      return users[event.player_user_id];
    }
    // Fallback: look up roster by contestant_id
    const roster = rosters.find(r => r.contestant_id === event.contestant_id);
    return roster ? users[roster.user_id] : null;
  };

  // Get all players for a contestant (for double draft support)
  const getPlayersForContestant = (contestantId) => {
    const contestantRosters = rosters.filter(r => r.contestant_id === contestantId);
    return contestantRosters.map(r => users[r.user_id]).filter(u => u);
  };

  // Format player display for event - shows all owners for double draft with colors
  const getPlayerDisplayForEvent = (event) => {
    // For BONUS events, use the specific player_user_id from the event
    if (event.contestant_id === 'BONUS') {
      const player = event.player_user_id ? users[event.player_user_id] : null;
      if (!player) return null;
      return (
        <div className="flex items-center gap-1">
          <div 
            className="w-3 h-3 rounded-full flex-shrink-0" 
            style={{ backgroundColor: player.color || '#3b82f6' }}
          />
          <span>{player.name}</span>
        </div>
      );
    }
    
    // For regular events, show ALL players who own this contestant (double draft support)
    const players = getPlayersForContestant(event.contestant_id);
    if (players.length === 0) {
      // Fallback to player_user_id if no roster found
      if (event.player_user_id) {
        const player = users[event.player_user_id];
        if (player) {
          return (
            <div className="flex items-center gap-1">
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0" 
                style={{ backgroundColor: player.color || '#3b82f6' }}
              />
              <span>{player.name}</span>
            </div>
          );
        }
      }
      return null;
    }
    
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {players.map((p, idx) => (
          <span key={p.id} className="flex items-center gap-1">
            {idx > 0 && <span className="text-slate-400">,</span>}
            <span 
              className="w-3 h-3 rounded-full flex-shrink-0 inline-block" 
              style={{ backgroundColor: p.color || '#3b82f6' }}
            />
            <span>{p.name}</span>
          </span>
        ))}
      </div>
    );
  };

  // Apply all filters
  const filteredEvents = events.filter(event => {
    // Filter by episodes
    if (filterEpisodes.length > 0 && !filterEpisodes.includes(event.episode_id)) {
      return false;
    }
    
    // Filter by players - for double draft, match if ANY owner is in the filter
    if (filterPlayers.length > 0) {
      if (event.contestant_id === 'BONUS') {
        // For bonus events, check player_user_id
        if (!event.player_user_id || !filterPlayers.includes(event.player_user_id)) {
          return false;
        }
      } else {
        // For regular events, check all owners
        const players = getPlayersForContestant(event.contestant_id);
        const hasMatchingOwner = players.some(p => filterPlayers.includes(p.id));
        // Fallback to player_user_id if no roster match
        const matchesFallback = event.player_user_id && filterPlayers.includes(event.player_user_id);
        if (!hasMatchingOwner && !matchesFallback) {
          return false;
        }
      }
    }
    
    // Filter by contestants
    if (filterContestants.length > 0 && !filterContestants.includes(event.contestant_id)) {
      return false;
    }
    
    // Filter by tribes
    if (filterTribes.length > 0 && event.tribe_id && !filterTribes.includes(event.tribe_id)) {
      return false;
    }
    
    return true;
  });
  
  const toggleFilter = (filterArray, setFilter, value) => {
    if (filterArray.includes(value)) {
      setFilter(filterArray.filter(v => v !== value));
    } else {
      setFilter([...filterArray, value]);
    }
  };

  const clearAllFilters = () => {
    setFilterPlayers([]);
    setFilterContestants([]);
    setFilterTribes([]);
    setFilterEpisodes([]);
  };

  return (
    <Layout>
      <PullToRefresh onRefresh={loadData}>
        <div className="max-w-7xl mx-auto container-mobile" data-testid="event-log">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Event Log
          </h1>
          <p className="text-responsive text-slate-600">Season {currentSeason?.number} Event History</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="padding-card">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                  {/* Players Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Player</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterPlayers.length === 0 
                              ? 'All Players' 
                              : filterPlayers.length === 1 
                                ? users[filterPlayers[0]]?.name || 'Selected'
                                : `${filterPlayers.length} Players`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {Object.values(users).map(u => (
                          <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterPlayers.includes(u.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleFilter(filterPlayers, setFilterPlayers, u.id);
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">{u.name}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Contestants Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Contestant</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterContestants.length === 0 
                              ? 'All Contestants' 
                              : filterContestants.length === 1 
                                ? contestants[filterContestants[0]]?.full_name || 'Selected'
                                : `${filterContestants.length} Contestants`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {Object.values(contestants).map(c => (
                          <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterContestants.includes(c.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleFilter(filterContestants, setFilterContestants, c.id);
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">{c.full_name}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Tribes Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Tribe</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterTribes.length === 0 
                              ? 'All Tribes' 
                              : filterTribes.length === 1 
                                ? tribes[filterTribes[0]]?.name || 'Selected'
                                : `${filterTribes.length} Tribes`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {Object.values(tribes).map(t => (
                          <label key={t.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterTribes.includes(t.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleFilter(filterTribes, setFilterTribes, t.id);
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">{t.name}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Episodes Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Episode</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterEpisodes.length === 0 
                              ? 'All Episodes' 
                              : filterEpisodes.length === 1 
                                ? `Episode ${episodes.find(e => e.id === filterEpisodes[0])?.number || '?'}`
                                : `${filterEpisodes.length} Episodes`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {episodes.map(ep => (
                          <label key={ep.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterEpisodes.includes(ep.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleFilter(filterEpisodes, setFilterEpisodes, ep.id);
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">Episode {ep.number}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Clear Filters Button */}
                  <div className="flex items-end">
                    <button
                      onClick={clearAllFilters}
                      disabled={filterPlayers.length === 0 && filterContestants.length === 0 && filterTribes.length === 0 && filterEpisodes.length === 0}
                      className="w-full px-4 py-2 text-sm font-medium text-amber-600 hover:text-amber-700 border border-amber-300 rounded-md hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Event Log Table */}
            <Card>
              <CardHeader className="padding-card pb-2 md:pb-4 block md:hidden">
                <CardTitle className="heading-card">Events</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Swipe left to see all details →</p>
              </CardHeader>
              <CardContent className="p-0">
                {filteredEvents.length === 0 ? (
                  <div className="text-center py-12 text-slate-600">
                    No events recorded yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="hidden md:table-cell">Episode</th>
                          <th className="md:hidden">Ep</th>
                          <th className="hidden sm:table-cell">Player</th>
                          <th>Contestant</th>
                          <th className="hidden sm:table-cell">Tribe</th>
                          <th>Event</th>
                          <th className="text-center hidden md:table-cell">Points</th>
                          <th className="text-center md:hidden">Pts</th>
                          <th className="hidden lg:table-cell">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map(event => {
                          const playerDisplay = getPlayerDisplayForEvent(event);
                          const contestantId = event.contestant_id;
                          
                          return (
                            <tr key={event.id}>
                              <td className="font-medium whitespace-nowrap">{getEpisodeNumber(event.episode_id)}</td>
                              <td className="hidden sm:table-cell whitespace-nowrap">
                                {playerDisplay || <span className="text-slate-400">-</span>}
                              </td>
                              <td>{getContestantName(contestantId)}</td>
                              <td className="hidden sm:table-cell">
                                {event.tribe_id && tribes[event.tribe_id] ? (
                                  <span 
                                    className="px-2 py-0.5 md:px-3 md:py-1 rounded-full text-white text-[10px] md:text-xs font-medium whitespace-nowrap"
                                    style={{ backgroundColor: tribes[event.tribe_id]?.color || '#64748b' }}
                                  >
                                    {tribes[event.tribe_id]?.name}
                                  </span>
                                ) : '-'}
                              </td>
                              <td>{event.event_name}</td>
                              <td className="text-center">
                                <span className={`font-bold ${
                                  event.points > 0 ? 'text-emerald-600' :
                                  event.points < 0 ? 'text-red-600' :
                                  'text-slate-600'
                                }`}>
                                  {event.points > 0 ? '+' : ''}{event.points}
                                </span>
                              </td>
                              <td className="hidden lg:table-cell text-slate-600 max-w-[150px] truncate">
                                {event.notes || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </PullToRefresh>
    </Layout>
  );
}
